import { learningDefaults } from "../constants/learningDefaults";

function stripHtml(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getGuidanceMediaUrl(guidance) {
  return guidance?.file_url || guidance?.resource_url || "";
}

function getGuidanceType(fileType) {
  return fileType === "video" ? "video" : "document";
}

function getGuidanceSubtitle(fileType) {
  return fileType === "video" ? "Video briefing" : "Document";
}

function getGuidanceMeta(item) {
  const {
    guidance_id: guidance,
    require_full_completion: requireFullCompletion,
  } = item;

  if (guidance.file_type === "video") {
    return requireFullCompletion ? "Video · no skip" : "Video";
  }

  return requireFullCompletion
    ? "Document · PDF · scroll to end"
    : "Document · PDF";
}

function getGuidanceInstruction(type, requireFullCompletion) {
  if (type === "video") {
    return requireFullCompletion
      ? "Watch in full — you cannot skip ahead"
      : "Watch the briefing, then continue";
  }

  return requireFullCompletion
    ? "Scroll to the end to continue"
    : "Review the document, then continue";
}

function formatQuestionCountSubtitle(count) {
  const total = Number(count);
  if (!Number.isFinite(total) || total < 0) {
    return "Knowledge check";
  }
  return `${total} question${total === 1 ? "" : "s"}`;
}

function mapGuidanceStep(item, index, total) {
  const guidance = item.guidance_id;
  const type = getGuidanceType(guidance.file_type);
  const requireFullCompletion = item.require_full_completion ?? true;
  const mediaUrl = getGuidanceMediaUrl(guidance);

  return {
    id: guidance._id,
    type,
    progressStepId: guidance._id,
    label: `TRAINING ${index + 1} OF ${total}`,
    stepNumber: index + 1,
    stepTotal: total,
    title: guidance.title,
    instruction: getGuidanceInstruction(type, requireFullCompletion),
    require_full_completion: requireFullCompletion,
    ...(type === "video"
      ? { videoSrc: mediaUrl }
      : {
          pdfSrc: mediaUrl,
          readTimeLabel: `${String(guidance.guidance_type || "document")
            .replace(/_/g, " ")
            .toUpperCase()} · PDF`,
        }),
  };
}

/** Maps `learning-development/:id` API payload to the L&D course shape used by the UI. */
export function mapLearningPackage(apiPackage) {
  const sortedGuidances = [...(apiPackage?.guidances || [])].sort(
    (left, right) => (left.order ?? 0) - (right.order ?? 0),
  );
  const guidanceCount = sortedGuidances.length;

  const guidanceSteps = sortedGuidances.map((item, index) =>
    mapGuidanceStep(item, index, guidanceCount),
  );

  const guidanceProgressSteps = sortedGuidances.map((item, index) => ({
    id: item.guidance_id._id,
    title: item.guidance_id.title,
    subtitle: getGuidanceSubtitle(item.guidance_id.file_type),
    stepNumber: index + 1,
  }));

  const questionCount = apiPackage?.survey_id?.question_count;

  const questionnaireStep = {
    id: "questionnaire",
    title: "Questionnaire",
    subtitle: formatQuestionCountSubtitle(questionCount),
    stepNumber: guidanceCount + 1,
    locked: true,
  };

  const confirmationStep = {
    id: "confirmation",
    title: "Confirmation",
    subtitle: "",
    stepNumber: guidanceCount + 2,
  };

  const passMarkPercent = apiPackage?.survey_id?.passing_score_percentage ?? 0; // using from API only

  const maxAttempts = apiPackage?.max_attempts ?? learningDefaults.maxAttempts;

  const whatsInvolved = [
    ...sortedGuidances.map((item) => ({
      id: item.guidance_id._id,
      title: item.guidance_id.title,
      meta: getGuidanceMeta(item),
      icon: item.guidance_id.file_type === "video" ? "video" : "document",
      locked: false,
    })),
    {
      id: "questionnaire",
      title: "Questionnaire",
      questionCount:
        Number.isFinite(Number(questionCount)) && Number(questionCount) > 0
          ? Number(questionCount)
          : null,
      passMarkPercent,
      icon: "lock",
      locked: true,
    },
  ];

  const description = stripHtml(apiPackage?.description);

  return {
    ...learningDefaults,
    packageId: apiPackage._id,
    companyId: apiPackage.company_id || null,
    surveyId: apiPackage?.survey_id?._id || null,
    surveyName: apiPackage?.survey_id?.name || null,
    questionCount: Number.isFinite(Number(questionCount))
      ? Number(questionCount)
      : null,
    passMarkPercent,
    maxAttempts,
    category: apiPackage?.status || null,
    dueDate: apiPackage?.due_date || null,
    title: stripHtml(apiPackage?.name) || "",
    description: description || "",
    whatsInvolved,
    progressSteps: [
      ...guidanceProgressSteps,
      questionnaireStep,
      confirmationStep,
    ],
    guidanceSteps,
  };
}
